import type {
  CertificationDetail,
  CertificationLifecycleStatus,
  CertificationProgress,
  ItemProgress,
  RequirementOutline,
  RequirementProgress,
  RequirementType,
} from "@/redux/certifications/types";
import {
  CountProgress,
  ModuleOverview,
  ModuleStatus,
  ModuleWeek,
} from "@/domains/certifications/types";
import { toKnowledgeCheckState } from "@/domains/certifications/knowledgeCheck";
import {
  CERTIFICATION_LIFECYCLE_STATUSES,
  KNOWLEDGE_CHECK_STATES,
  MODULE_STATUSES,
  REQUIREMENT_TYPES,
} from "@/domains/certifications/constants";

interface TrackedRequirement {
  requirement: RequirementOutline;
  progress: RequirementProgress | undefined;
}

const requirementsForTrack = (
  certification: CertificationDetail,
  trackId: number,
): RequirementOutline[] => {
  const unique = new Map<string, RequirementOutline>();
  certification.requirementGroups
    .filter((group) => group.track === trackId)
    .forEach((group) =>
      group.requirements.forEach((requirement) =>
        unique.set(
          `${requirement.type}:${requirement.contentItem}`,
          requirement,
        ),
      ),
    );
  return Array.from(unique.values());
};

export const countTrackRequirements = (
  certification: CertificationDetail,
  trackId: number,
) => requirementsForTrack(certification, trackId).length;

export const countTrackRequirementsOfType = (
  certification: CertificationDetail,
  trackId: number,
  type: RequirementType,
) =>
  requirementsForTrack(certification, trackId).filter(
    (requirement) => requirement.type === type,
  ).length;

const isSatisfied = (tracked: TrackedRequirement) =>
  Boolean(tracked.progress?.isSatisfied);

const toCount = (
  tracked: TrackedRequirement | undefined,
): CountProgress | null =>
  tracked?.progress
    ? {
        current: Number(tracked.progress.currentValue),
        target: Number(tracked.progress.targetValue),
      }
    : null;

const toItemCount = (tracked: TrackedRequirement[]): CountProgress | null =>
  tracked.length
    ? { current: tracked.filter(isSatisfied).length, target: tracked.length }
    : null;

export const isProgressSettled = (
  status: CertificationLifecycleStatus,
  weeks: ModuleOverview[],
) =>
  status === CERTIFICATION_LIFECYCLE_STATUSES.UPCOMING ||
  weeks.length === 0 ||
  weeks.some((week) => week.status === MODULE_STATUSES.CURRENT) ||
  weeks.every((week) => week.status === MODULE_STATUSES.COMPLETE);

export const buildModuleOverviews = (
  certification: CertificationDetail,
  progress: CertificationProgress,
  trackId: number,
): ModuleOverview[] => {
  const requirements = requirementsForTrack(certification, trackId);
  const progressByKey = new Map(
    progress.requirements.map((item) => [item.requirementKey, item]),
  );
  const moduleProgressById = new Map(
    progress.modules.map((module) => [module.id, module]),
  );
  const orderedModules = [...certification.modules].sort(
    (a, b) => a.order - b.order,
  );

  let hasCurrent = false;

  return orderedModules.map((module, index) => {
    const itemIds = new Set(module.items.map((item) => item.id));
    const moduleProgress = moduleProgressById.get(module.id);
    const itemProgressById = new Map<number, ItemProgress>(
      (moduleProgress?.items ?? []).map((item) => [item.id, item]),
    );
    const tracked: TrackedRequirement[] = requirements
      .filter((requirement) => itemIds.has(requirement.contentItem))
      .map((requirement) => ({
        requirement,
        progress: progressByKey.get(requirement.key),
      }));
    const ofType = (type: RequirementType) =>
      tracked.filter((item) => item.requirement.type === type);

    const videos = ofType(REQUIREMENT_TYPES.VIDEO_COMPLETION);
    const readings = ofType(REQUIREMENT_TYPES.RESOURCE_DOWNLOAD);
    const check = ofType(REQUIREMENT_TYPES.KNOWLEDGE_CHECK_PASS)[0];
    const checkAttempt = check
      ? itemProgressById.get(check.requirement.contentItem)?.knowledgeCheck
      : undefined;
    const checkState = checkAttempt?.state ?? null;

    const isUnlocked = moduleProgress?.isUnlocked ?? false;
    const isComplete = isUnlocked && tracked.every(isSatisfied);
    const isStarted =
      (moduleProgress?.items ?? []).some(
        (item) => Number(item.video?.coveredPercent ?? 0) > 0,
      ) ||
      checkState !== null ||
      tracked.some((item) => Number(item.progress?.currentValue ?? 0) > 0);

    let status: ModuleStatus = MODULE_STATUSES.LOCKED;
    if (isComplete) {
      status = MODULE_STATUSES.COMPLETE;
    } else if (isUnlocked && !hasCurrent) {
      status = MODULE_STATUSES.CURRENT;
      hasCurrent = true;
    } else if (isUnlocked) {
      status = MODULE_STATUSES.AVAILABLE;
    }

    return {
      module,
      position: index + 1,
      status,
      isStarted,
      videos: toItemCount(videos),
      readings: toItemCount(readings),
      itemSatisfaction: Object.fromEntries(
        [...videos, ...readings].map((item) => [
          item.requirement.contentItem,
          isSatisfied(item),
        ]),
      ),
      knowledgeCheck: check
        ? {
            isPassed: isSatisfied(check),
            checkState: isSatisfied(check)
              ? KNOWLEDGE_CHECK_STATES.PASSED
              : toKnowledgeCheckState(
                  checkState,
                  checkAttempt?.attemptsRemaining ?? 0,
                ),
          }
        : null,
      posts: toCount(ofType(REQUIREMENT_TYPES.COMMUNITY_POST_COUNT)[0]),
      comments: toCount(ofType(REQUIREMENT_TYPES.COMMUNITY_COMMENT_COUNT)[0]),
    };
  });
};

export const findModuleWeek = (
  weeks: ModuleOverview[],
  moduleId: number,
): ModuleWeek | null => {
  const index = weeks.findIndex((week) => week.module.id === moduleId);
  const week = weeks[index];
  return week ? { week, nextWeek: weeks[index + 1] ?? null } : null;
};
